import { useState, useEffect, useRef } from "react";

export type ScrollDirection = "up" | "down" | null;

interface UseScrollDirectionOptions {
  /** Minimum scroll delta in pixels before triggering direction change (prevents jitter) */
  threshold?: number;
  /** Always show header when within this many pixels from top of page */
  topOffset?: number;
}

export function useScrollDirection({
  threshold = 10,
  topOffset = 100,
}: UseScrollDirectionOptions = {}): ScrollDirection {
  const [scrollDirection, setScrollDirection] = useState<ScrollDirection>(null);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    lastScrollY.current = globalThis.scrollY;

    const updateScrollDirection = () => {
      const scrollY = globalThis.scrollY;

      // Always show header near top of page
      if (scrollY < topOffset) {
        setScrollDirection(null);
        lastScrollY.current = scrollY;
        ticking.current = false;
        return;
      }

      const delta = scrollY - lastScrollY.current;

      if (Math.abs(delta) > threshold) {
        setScrollDirection(delta > 0 ? "down" : "up");
        lastScrollY.current = scrollY;
      }

      ticking.current = false;
    };

    const onScroll = () => {
      if (!ticking.current) {
        globalThis.requestAnimationFrame(updateScrollDirection);
        ticking.current = true;
      }
    };

    globalThis.addEventListener("scroll", onScroll, { passive: true });
    return () => globalThis.removeEventListener("scroll", onScroll);
  }, [threshold, topOffset]);

  return scrollDirection;
}
